import type { ReactNode } from "react";
import { Trans, useLingui } from "@lingui/react/macro";
import { Link } from "@tanstack/react-router";
import { ArrowLeft, UserRound, Lock, LogIn, Activity } from "lucide-react";
import { useIsTablet } from "@/hooks/use-mobile";
import { PageHeader } from "@/components/layout/PageHeaderSlot";
import { cn } from "@/lib/utils";
import { ProfileActionsMenu } from "./ProfileActionsMenu";

interface ProfileHeaderProps {
  name: string;
  email: string;
  initials: string;
  avatarUrl?: string;
  statusLabel: string;
}

export function ProfileHeader({
  name,
  email,
  initials,
  avatarUrl,
  statusLabel,
}: ProfileHeaderProps) {
  const { t } = useLingui();
  const isTablet = useIsTablet();

  // On tablet the shell hides the TopBar and lets the page define its own
  // compact, page-specific header (matches the other detail pages).
  if (isTablet) {
    return (
      <PageHeader>
        <div className="flex h-15.5 items-center gap-3 border-b border-(--color-border) px-5">
          <Link
            to="/dashboard"
            title={t`Back to Dashboard`}
            className="flex size-11 shrink-0 items-center justify-center rounded-xl border border-(--color-border) bg-(--color-surface-content) text-(--color-text-primary) transition-colors hover:bg-(--color-surface-muted)"
          >
            <ArrowLeft className="size-5" />
          </Link>
          <h1 className="truncate text-[1.375rem] font-extrabold leading-none tracking-tight text-(--color-text-primary)">
            {name}
          </h1>
          <span className="inline-flex shrink-0 items-center rounded-full bg-(--color-success-bg) px-2.5 py-0.5 text-[0.6875rem] font-bold text-(--color-success-text)">
            {statusLabel}
          </span>
          <div className="ml-auto flex shrink-0 items-center gap-2">
            <ProfileActionsMenu email={email} />
          </div>
        </div>
      </PageHeader>
    );
  }

  return (
    <div className="flex flex-col gap-5">
      <Link
        to="/dashboard"
        className="flex w-fit items-center gap-2 rounded-lg px-3 py-2 text-base font-medium text-(--color-text-secondary) transition-colors hover:bg-(--color-surface-accent)"
      >
        <ArrowLeft className="size-5" />
        <Trans>Back to Dashboard</Trans>
      </Link>

      <div className="flex flex-col gap-5 px-3 md:flex-row md:items-center md:justify-between">
        <div className="flex flex-col gap-4 md:flex-row md:items-center md:gap-6">
          {avatarUrl ? (
            <img
              src={avatarUrl}
              alt={name}
              className="size-28 shrink-0 rounded-2xl object-cover md:size-40"
            />
          ) : (
            <div className="flex size-28 shrink-0 items-center justify-center rounded-2xl bg-(--color-avatar-magenta-bg) md:size-40">
              <span className="text-4xl font-medium tracking-tight text-(--color-avatar-magenta) md:text-[3.75rem]">
                {initials}
              </span>
            </div>
          )}

          <div className="flex flex-col gap-2">
            <div className="flex flex-wrap items-center gap-3">
              <h1 className="text-4xl font-bold leading-none tracking-tight text-(--color-text-primary) md:text-5xl">
                {name}
              </h1>
              <span className="inline-flex items-center rounded-full bg-(--color-success-bg) px-3 py-1 text-sm font-semibold text-(--color-success-text)">
                {statusLabel}
              </span>
            </div>
            <p className="text-base text-(--color-text-secondary)">{email}</p>
          </div>
        </div>

        <ProfileActionsMenu email={email} />
      </div>
    </div>
  );
}

interface ProfileStatCardsProps {
  role: string;
  accessScope: string;
  lastLogin: string;
  status: string;
}

export function ProfileStatCards({
  role,
  accessScope,
  lastLogin,
  status,
}: ProfileStatCardsProps) {
  const { t } = useLingui();

  return (
    <div className="grid grid-cols-1 gap-2 rounded-xl bg-(--color-surface-muted) p-2 md:grid-cols-2 xl:grid-cols-4">
      <StatCell icon={<UserRound className="size-5" />} label={t`Role`} value={role} />
      <StatCell
        icon={<Lock className="size-5" />}
        label={t`Access Scope`}
        value={accessScope}
      />
      <StatCell
        icon={<LogIn className="size-5" />}
        label={t`Last Login`}
        value={lastLogin}
      />
      <StatCell
        icon={<Activity className="size-5" />}
        label={t`Status`}
        value={status}
      />
    </div>
  );
}

function StatCell({
  icon,
  label,
  value,
  className,
}: {
  icon: ReactNode;
  label: string;
  value: ReactNode;
  className?: string;
}) {
  return (
    <div
      className={cn(
        "rounded-[10px] border border-(--color-border) bg-(--color-surface-content) p-4",
        className,
      )}
    >
      <div className="flex flex-col gap-2">
        <div className="flex items-center gap-2">
          <div className="flex size-8 shrink-0 items-center justify-center rounded-md bg-(--color-surface-muted) text-(--color-text-secondary)">
            {icon}
          </div>
          <span className="text-sm font-medium text-(--color-text-secondary)">
            {label}
          </span>
        </div>
        <span className="truncate text-base font-medium text-(--color-text-primary)">
          {value}
        </span>
      </div>
    </div>
  );
}
